// Opt-in Christmas music: the full "Jingle Bells" (public-domain melody) as a soft music box, synthesized with Web Audio.
// Never autoplays on a first visit; it starts only when the shopper presses the music button. Once on, it keeps playing
// on every page of the visit, continuing from the same spot in the song, until the shopper switches it off with the button.
// Browsers silence a page until it has been touched, so on each new page the music continues on the shopper's first
// tap, click or key press.
const STATE_KEY = 'nicepick-christmas-music'; // 'on' | 'off'
const START_KEY = 'nicepick-christmas-music-start'; // epoch ms of song position 0, so pages share one timeline


// Full song: verse + chorus, second verse + chorus. [note, beats] — 0 is a rest. Quarter note = 1 beat.
const VERSE = [
  ['G4', 1], ['E5', 1], ['D5', 1], ['C5', 1], ['G4', 3], ['G4', 0.5], ['G4', 0.5],
  ['G4', 1], ['E5', 1], ['D5', 1], ['C5', 1], ['A4', 4],
  ['A4', 1], ['F5', 1], ['E5', 1], ['D5', 1], ['B4', 4],
  ['G5', 1], ['G5', 1], ['F5', 1], ['D5', 1], ['E5', 4],
  ['G4', 1], ['E5', 1], ['D5', 1], ['C5', 1], ['G4', 4],
  ['G4', 1], ['E5', 1], ['D5', 1], ['C5', 1], ['A4', 3], ['A4', 1],
  ['A4', 1], ['F5', 1], ['E5', 1], ['D5', 1], ['G5', 1], ['G5', 1], ['G5', 1], ['G5', 1],
  ['A5', 1], ['G5', 1], ['F5', 1], ['D5', 1], ['C5', 4],
];
const CHORUS = [
  ['E5', 1], ['E5', 1], ['E5', 2], ['E5', 1], ['E5', 1], ['E5', 2],
  ['E5', 1], ['G5', 1], ['C5', 1.5], ['D5', 0.5], ['E5', 4],
  ['F5', 1], ['F5', 1], ['F5', 1.5], ['F5', 0.5], ['F5', 1], ['E5', 1], ['E5', 1], ['E5', 0.5], ['E5', 0.5],
  ['E5', 1], ['D5', 1], ['D5', 1], ['E5', 1], ['D5', 2], ['G5', 2],
  ['E5', 1], ['E5', 1], ['E5', 2], ['E5', 1], ['E5', 1], ['E5', 2],
  ['E5', 1], ['G5', 1], ['C5', 1.5], ['D5', 0.5], ['E5', 4],
  ['F5', 1], ['F5', 1], ['F5', 1.5], ['F5', 0.5], ['F5', 1], ['E5', 1], ['E5', 1], ['E5', 0.5], ['E5', 0.5],
  ['G5', 1], ['G5', 1], ['F5', 1], ['D5', 1], ['C5', 4],
];
const MELODY = [...VERSE, ...CHORUS, [0, 2], ...VERSE, ...CHORUS, [0, 6]]; // trailing rest = pause before the song repeats
const FREQ = { G4: 392.0, A4: 440.0, B4: 493.88, C5: 523.25, D5: 587.33, E5: 659.25, F5: 698.46, G5: 783.99, A5: 880.0 };
const BEAT = 0.3; // seconds per beat


const NOTE_TIMES = [];
let SONG_LENGTH = 0;
for (const [, beats] of MELODY) {
  NOTE_TIMES.push(SONG_LENGTH);
  SONG_LENGTH += beats * BEAT;
}
const LOOKAHEAD = 1.5; // seconds of notes scheduled ahead (covers background-tab timer throttling)
const ACTIVATION_EVENTS = ['pointerup', 'touchend', 'click', 'keydown'];

const store = {
  get: (key) => {
    try { return localStorage.getItem(key); } catch { return null; }
  },
  set: (key, value) => {
    try { localStorage.setItem(key, value); } catch { /* storage blocked: music still works for this page */ }
  },
};

class ChristmasMusic extends HTMLElement {
  #ctx = null;
  #master = null;
  #interval = 0;
  #playing = false;
  #button = null;
  #songTime0 = 0;
  #nextIndex = 0;
  #resumedAt = 0;
  #onActivation = () => {
    if (store.get(STATE_KEY) !== 'on' || this.#playing) return;
    this.#resumedAt = performance.now();
    this.#play();
  };
  #onPageShow = (event) => {
    if (event.persisted && store.get(STATE_KEY) === 'on') {
      this.#stopAudio();
      this.#armResume();
    }
  };

  connectedCallback() {
    this.#button = this.querySelector('button');
    this.#button?.addEventListener('click', () => {
      // The same tap may have just resumed the music on a new page; don't immediately switch it off again.
      if (performance.now() - this.#resumedAt < 600) return;
      if (this.#playing || store.get(STATE_KEY) === 'on') this.#switchOff();
      else this.#switchOn();
    });
    window.addEventListener('pageshow', this.#onPageShow);
    // The "Tap for music" hint shows for a few seconds per page so it never sits over buttons on small screens.
    window.setTimeout(() => this.toggleAttribute('data-hint-hidden', true), 7000);
    if (store.get(STATE_KEY) === 'on') this.#armResume();
    this.#render();
  }

  disconnectedCallback() {
    window.removeEventListener('pageshow', this.#onPageShow);
    this.#disarmResume();
    this.#stopAudio();
  }

  #switchOn() {
    store.set(STATE_KEY, 'on');
    store.set(START_KEY, String(Date.now()));
    this.#resumedAt = performance.now();
    this.#play();
  }

  #switchOff() {
    store.set(STATE_KEY, 'off');
    this.#disarmResume();
    this.#stopAudio();
    this.#render();
  }

  #armResume() {
    for (const type of ACTIVATION_EVENTS) document.addEventListener(type, this.#onActivation, { capture: true, passive: true });
  }

  #disarmResume() {
    for (const type of ACTIVATION_EVENTS) document.removeEventListener(type, this.#onActivation, { capture: true });
  }

  #play() {
    const AudioCtx = window.AudioContext || window.webkitAudioContext;
    if (!AudioCtx) return;
    // iPhone: let the music play even when the ring/silent switch is on silent (Safari 17+).
    try { if (navigator.audioSession) navigator.audioSession.type = 'playback'; } catch { /* unsupported */ }
    this.#ctx = new AudioCtx();
    this.#ctx.resume();
    this.#master = this.#ctx.createGain();
    this.#master.gain.value = 0.07;
    this.#master.connect(this.#ctx.destination);

    // Continue from the shared timeline so the song carries on across pages instead of restarting.
    const startedAt = Number(store.get(START_KEY)) || Date.now();
    const offset = (((Date.now() - startedAt) / 1000) % SONG_LENGTH + SONG_LENGTH) % SONG_LENGTH;
    this.#songTime0 = this.#ctx.currentTime + 0.05 - offset;
    this.#nextIndex = NOTE_TIMES.findIndex((t) => t >= offset);
    if (this.#nextIndex === -1) {
      this.#nextIndex = 0;
      this.#songTime0 += SONG_LENGTH;
    }

    this.#playing = true;
    this.#disarmResume();
    this.#tick();
    this.#interval = window.setInterval(() => this.#tick(), 250);
    this.#render();
  }

  #stopAudio() {
    window.clearInterval(this.#interval);
    if (this.#ctx) this.#ctx.close();
    this.#ctx = null;
    this.#playing = false;
  }

  // Lookahead scheduler: queue the notes due in the next LOOKAHEAD seconds; the song repeats until switched off.
  #tick() {
    if (!this.#ctx) return;
    const horizon = this.#ctx.currentTime + LOOKAHEAD;
    while (this.#songTime0 + NOTE_TIMES[this.#nextIndex] < horizon) {
      const [note] = MELODY[this.#nextIndex];
      const time = this.#songTime0 + NOTE_TIMES[this.#nextIndex];
      if (note && time >= this.#ctx.currentTime - 0.05) this.#bell(FREQ[note], Math.max(time, this.#ctx.currentTime));
      this.#nextIndex += 1;
      if (this.#nextIndex >= MELODY.length) {
        this.#nextIndex = 0;
        this.#songTime0 += SONG_LENGTH;
      }
    }
  }

  // Music-box bell: sine fundamental + quiet upper partials with a fast attack and soft decay.
  #bell(freq, time) {
    const ctx = this.#ctx;
    const partials = [[1, 1], [2, 0.35], [3, 0.12]];
    for (const [multiple, level] of partials) {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'sine';
      osc.frequency.value = freq * multiple;
      gain.gain.setValueAtTime(0.0001, time);
      gain.gain.exponentialRampToValueAtTime(level, time + 0.01);
      gain.gain.exponentialRampToValueAtTime(0.0001, time + 1.1);
      osc.connect(gain).connect(this.#master);
      osc.start(time);
      osc.stop(time + 1.2);
    }
  }

  #render() {
    const on = this.#playing || store.get(STATE_KEY) === 'on';
    this.toggleAttribute('data-chosen', store.get(STATE_KEY) !== null);
    if (!this.#button) return;
    this.#button.setAttribute('aria-pressed', String(on));
    this.#button.setAttribute('aria-label', on ? this.dataset.pauseLabel : this.dataset.playLabel);
    this.#button.title = this.#button.getAttribute('aria-label');
  }
}

if (!customElements.get('s-christmas-music')) customElements.define('s-christmas-music', ChristmasMusic);
